import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthStore } from '@/lib/auth';
import { useSyncStore } from './store';

interface SyncContextType {
  refreshSyncDates: () => Promise<void>;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export function SyncProvider({ children }: { children: ReactNode }) {
  const refreshSyncDates = useSyncStore((state) => state.refreshSyncDates);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Fetch once logged in (the endpoint requires auth, so a fetch before login just fails)
  useEffect(() => {
    if (isAuthenticated) refreshSyncDates();
  }, [isAuthenticated, refreshSyncDates]);

  // Pick up syncs made elsewhere (scheduled job, other users) when the user returns to the tab
  useEffect(() => {
    if (!isAuthenticated) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshSyncDates();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, refreshSyncDates]);

  return <SyncContext.Provider value={{ refreshSyncDates }}>{children}</SyncContext.Provider>;
}

export function useSync() {
  const context = useContext(SyncContext);
  if (context === undefined) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}
